/**
 * ConfirmDialog - Modern functional React component for confirmation dialogs
 *
 * This component replicates the exact visual appearance and behavior of the
 * legacy context.confirm system while using modern React patterns.
 *
 * Uses the same CSS classes as the old implementation for perfect visual parity.
 */

import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

const ConfirmDialog = ({
    contents,
    acceptText,
    rejectText,
    titleText,
    position,
    rejectDefaultClass,
    onResolve
}) => {
    // Handle escape key and backdrop clicks as rejection
    const handleReject = useCallback(() => {
        onResolve(false);
    }, [onResolve]);

    const handleAccept = useCallback(() => {
        onResolve(true);
    }, [onResolve]);

    // Handle keyboard events
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleReject();
            } else if (event.key === 'Enter') {
                // Only auto-accept on Enter if there's no reject button
                if (!rejectText) {
                    handleAccept();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleReject, handleAccept, rejectText]);

    const confirmDialogPositionClass = `el-modal3-${position}`;

    return (
        <Modal
            isOpen={true}
            onRequestClose={rejectText ? handleReject : handleAccept}
            closeModal={() => false}
            contentLabel="Confirmation"
            className={`el-modal el-modal3 ${confirmDialogPositionClass}`}
            overlayClassName={`el-modal-overlay ${confirmDialogPositionClass}`}
            closeTimeoutMS={250}
            appElement={document.getElementById('root') || document.body} // Set app element for accessibility
        >
            <section className="el-modal-container el-modal3-container">
                {titleText && (
                    <header>
                        <h2>{titleText}</h2>
                    </header>
                )}

                <div className="el-modal-body-container el-modal3-body-container el-fonts el-confirmation">
                    {typeof contents === 'string' ? (
                        <p className="p3">{contents}</p>
                    ) : null}

                    <div className="el-confirmation">
                        {contents && typeof contents !== 'string' ? (
                            contents
                        ) : null}
                    </div>
                </div>

                <footer>
                    {rejectText && (
                        <button
                            className={rejectDefaultClass || "el-modal-cancel-btn"}
                            onClick={handleReject}
                        >
                            {rejectText}
                        </button>
                    )}

                    {acceptText && (
                        <button
                            className="el-modal-ok-btn"
                            onClick={handleAccept}
                        >
                            {acceptText}
                        </button>
                    )}
                </footer>
            </section>
        </Modal>
    );
};

ConfirmDialog.propTypes = {
    contents: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    acceptText: PropTypes.string,
    rejectText: PropTypes.string,
    titleText: PropTypes.string,
    position: PropTypes.string,
    rejectDefaultClass: PropTypes.string,
    onResolve: PropTypes.func.isRequired,
};

ConfirmDialog.defaultProps = {
    acceptText: 'Ok',
    rejectText: 'Cancel',
    titleText: '',
    position: 'centered',
    rejectDefaultClass: null,
};

export default ConfirmDialog;
